import React, { useState } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import styled, { css } from 'styled-components';

import calendarIcon from '../../asset/img/common/calendar_icon.svg';

const DatePicker = ({ onChange, value, name, label, endPosition = 'center', disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');

  // value가 변경되면 inputValue 동기화
  React.useEffect(() => {
    setInputValue(formatDateForDisplay(value));
  }, [value]);

  const handleDateClick = date => {
    if (disabled) return;
    const formattedDate = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(
      2,
      '0',
    )}${String(date.getDate()).padStart(2, '0')}`;

    onChange({
      target: {
        name: name,
        value: formattedDate,
      },
    });
    setIsOpen(false);
  };

  // 표시용 포맷: YYYYMMDD -> YYYY-MM-DD
  const formatDateForDisplay = date => {
    if (!date || date === '') return '';
    const dateStr = String(date).replace(/-/g, '');
    if (dateStr.length === 8) {
      return `${dateStr.slice(0, 4)}-${dateStr.slice(4, 6)}-${dateStr.slice(6, 8)}`;
    }
    return date;
  };

  // 입력값 변경 핸들러
  const handleInputChange = e => {
    let val = e.target.value;

    // 숫자와 하이픈만 허용
    val = val.replace(/[^0-9-]/g, '');

    // 자동 하이픈 추가
    const numbers = val.replace(/-/g, '');
    if (numbers.length <= 4) {
      val = numbers;
    } else if (numbers.length <= 6) {
      val = `${numbers.slice(0, 4)}-${numbers.slice(4)}`;
    } else {
      val = `${numbers.slice(0, 4)}-${numbers.slice(4, 6)}-${numbers.slice(6, 8)}`;
    }

    setInputValue(val);
  };

  // 입력 완료 시 (blur) 값 전달
  const handleInputBlur = () => {
    const numbers = inputValue.replace(/-/g, '');
    if (numbers.length === 8) {
      onChange({
        target: {
          name: name,
          value: numbers,
        },
      });
    }
  };

  // 캘린더 아이콘 클릭
  const handleCalendarIconClick = e => {
    e.stopPropagation();
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <DatePickerContainer>
      {label && <Label>{label}</Label>}
      <DatePickerWrapper>
        <DateInput
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          placeholder="YYYY-MM-DD"
          name={name}
          disabled={disabled}
          maxLength={10}
        />
        <CalendarIconWrapper onClick={handleCalendarIconClick}>
          <img src={calendarIcon} alt="날짜선택" />
        </CalendarIconWrapper>
        {isOpen && (
          <CalendarWrapper endPosition={endPosition}>
            <Calendar
              onChange={handleDateClick}
              value={value}
              locale="ko-KR"
              defaultActiveStartDate={new Date(2025, 11)}
              next2Label={null}
              prev2Label={null}
              showNeighboringMonth={false}
              calendarType="gregory"
              formatDay={(locale, date) => date.getDate()}
              formatMonthYear={(locale, date) => `${date.getFullYear()}년 ${date.getMonth() + 1}월`}
            />
          </CalendarWrapper>
        )}
      </DatePickerWrapper>
    </DatePickerContainer>
  );
};

const DatePickerContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const Label = styled.label`
  color: var(--GrayScale-700);
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
`;

const DatePickerWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const DateInput = styled.input`
  width: 100%;
  padding: 8px 32px 8px 8px;
  border: 1px solid #ddd;
  border-radius: 5px;
  border: 1px solid ${props => (props.disabled ? 'var(--GrayScale-300)' : 'var(--GrayScale-400)')};
  background: ${props => (props.disabled ? 'var(--GrayScale-100)' : 'var(--GrayScale-0)')};
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;

  &:focus {
    border: 1px solid var(--Primary-500);
    outline: none;
  }
`;

const CalendarIconWrapper = styled.div`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;

  img {
    width: 20px;
    height: 20px;
  }
`;

const CalendarWrapper = styled.div.withConfig({
  shouldForwardProp: prop => !['endPosition'].includes(prop),
})`
  position: absolute;
  border: 1px solid rgb(221, 221, 221);
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  z-index: 100;
  margin-top: 4px;
  background: #ffffff;
  box-sizing: border-box;
  width: 320px;
  display: flex;
  justify-content: center;

  ${props => {
    if (props.endPosition === 'right') {
      return css`
        right: 0;
        left: auto;
        transform: none;
      `;
    }
    if (props.endPosition === 'left') {
      return css`
        left: 0;
        right: auto;
        transform: none;
      `;
    }
  }}

  abbr {
    font-size: 14px;
  }

  .react-calendar {
    width: 100%;
    border: none;
    padding: 16px;
    background: white;
    font-family: 'Pretendard', sans-serif;
    line-height: 1.125em;
  }

  .react-calendar__navigation {
    height: 40px;
    margin-bottom: 16px;
  }

  .react-calendar__navigation button {
    min-width: 44px;
    background: none;
    font-size: 16px;
    font-weight: bold;
  }

  .react-calendar__month-view__weekdays {
    text-align: center;
    text-transform: uppercase;
    font-weight: bold;
    font-size: 12px;
    margin-bottom: 8px;
  }

  .react-calendar__month-view__days__day {
    padding: 4px;
    color: #333;
    font-size: 14px;
  }

  .react-calendar__month-view__days__day--weekend {
    color: #d10000;
  }

  .react-calendar__month-view__days__day--neighboringMonth {
    color: #757575;
  }

  .react-calendar__tile {
    max-width: 100%;
    text-align: center;
    padding: 10px;
    background: none;
    border-radius: 50%;
    font-size: 12px;

    &:enabled:hover,
    &:enabled:focus {
      background: var(--Primary-50);
      color: var(--Primary-600);
      border-radius: 50%;
    }
  }

  .react-calendar__tile--active {
    background: var(--Primary-600) !important;
    color: white !important;
    border-radius: 50%;

    &:enabled:hover,
    &:enabled:focus {
      background: var(--Primary-700) !important;
      color: white !important;
    }
  }

  .react-calendar__tile--now {
    background: var(--Primary-50);
    color: var(--Primary-600);
    border-radius: 50%;
  }

  abbr[title] {
    text-decoration: none;
  }

  /* 년도 선택 타일 스타일 */
  .react-calendar__decade-view__years__year,
  .react-calendar__year-view__months__month {
    &:enabled:hover,
    &:enabled:focus {
      background: var(--Primary-50) !important;
      color: var(--Primary-600) !important;
    }
  }

  .react-calendar__decade-view__years__year--active,
  .react-calendar__year-view__months__month--active {
    background: var(--Primary-600) !important;
    color: white !important;

    &:enabled:hover,
    &:enabled:focus {
      background: var(--Primary-700) !important;
      color: white !important;
    }
  }
`;

export default DatePicker;
